import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Webpage } from '@/types';
import { supabase } from '@/lib/supabase';

// Global cache to track loaded image URLs across component instances
const loadedImageCache = new Set<string>();
import { Favicon } from '@/components/webpage/Favicon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from '@/components/ui/carousel';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DotsThree, PencilSimple, Trash, X, Check, Spinner, Link, ArrowSquareOut, Command, ArrowFatUp, ArrowElbowDownLeft, ArrowsOut } from '@phosphor-icons/react';
import { DeleteWebpageDialog } from './DeleteWebpageDialog';
import { useWebpages } from '@/contexts/WebpagesContext';
import { useToast } from '@/components/ui/use-toast';

interface WebpageDetailsProps {
  webpage: Webpage | null;
  onWebpageDeleted?: () => void;
  /** External control for edit mode (used on mobile) */
  isEditingExternal?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** External control for dropdown open state */
  isDropdownOpenExternal?: boolean;
  onDropdownOpenChangeExternal?: (open: boolean) => void;
  /** Hide the dropdown menu (used on mobile where FAB handles actions) */
  hideMenu?: boolean;
  /** Show menu only when webpage is actively selected (not just hovered) */
  isSelected?: boolean;
  /** Whether to show floating action buttons at bottom (for mobile) */
  showFloatingActions?: boolean;
  /** Callback when dropdown menu open state changes (for tracking purposes) */
  onDropdownOpenChange?: (open: boolean) => void;
  /** Whether to show hints at reduced opacity (for 4th item) */
  isHintFaded?: boolean;
}

export function WebpageDetails({ 
  webpage, 
  onWebpageDeleted,
  isEditingExternal,
  onEditingChange,
  isDropdownOpenExternal,
  onDropdownOpenChangeExternal,
  hideMenu = false,
  isSelected = true,
  showFloatingActions = false,
  onDropdownOpenChange,
  isHintFaded = false,
}: WebpageDetailsProps) {
  const [isImageVisible, setIsImageVisible] = useState(true);
  const [faviconReset, setFaviconReset] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  
  // Check if image is already in cache (loaded before)
  const isImageAlreadyCached = useMemo(() => {
    return imageUrl ? loadedImageCache.has(imageUrl) : false;
  }, [imageUrl]);
  
  // Extract and deduplicate images from markdown content
  const contentImages = useMemo(() => {
    if (!webpage?.content) return [];
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    const seen = new Set<string>();
    const images: { alt: string; src: string }[] = [];
    let match;
    while ((match = imageRegex.exec(webpage.content)) !== null) {
      if (!seen.has(match[2])) {
        seen.add(match[2]);
        images.push({ alt: match[1], src: match[2] });
      }
    }
    return images;
  }, [webpage?.content]);
  
  const [isImageLoaded, setIsImageLoaded] = useState(isImageAlreadyCached);
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [internalIsDropdownOpen, setInternalIsDropdownOpen] = useState(false);
  
  // Lightbox state for viewing images full-screen
  const [lightboxImageIndex, setLightboxImageIndex] = useState<number | null>(null);
  
  // Scroll detection for sticky header shadow
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Reset lightbox when content changes to prevent stale index
  useEffect(() => {
    setLightboxImageIndex(null);
  }, [webpage?.id, webpage?.content]);
  
  // Scroll detection for sticky header shadow
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    
    // Reset scroll state when element changes
    setIsScrolled(el.scrollTop > 0);
    
    const handleScroll = () => setIsScrolled(el.scrollTop > 0);
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [webpage?.id]); // Re-attach when webpage changes
  
  // Reset scroll position and shadow when webpage changes
  useEffect(() => {
    setIsScrolled(false);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [webpage?.id]);
  
  // Use external dropdown state if provided, otherwise use internal
  const isDropdownOpen = isDropdownOpenExternal !== undefined ? isDropdownOpenExternal : internalIsDropdownOpen;
  const setIsDropdownOpen = onDropdownOpenChangeExternal ?? setInternalIsDropdownOpen;
  
  // Use external editing state if provided, otherwise use internal
  const isEditing = isEditingExternal !== undefined ? isEditingExternal : internalIsEditing;
  const setIsEditing = onEditingChange ?? setInternalIsEditing;
  
  // Edit form state
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedSiteName, setEditedSiteName] = useState('');
  const [editedUrl, setEditedUrl] = useState('');
  const [editedAuthor, setEditedAuthor] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  const [editedContent, setEditedContent] = useState('');
  
  const { updateWebpage, deleteWebpage } = useWebpages();
  const { toast } = useToast();
  
  // Resolve image URL from storage path or original image
  useEffect(() => {
    if (webpage?.image_storage_path) {
      const { data } = supabase.storage
        .from('webpage-assets')
        .getPublicUrl(webpage.image_storage_path);
      setImageUrl(data.publicUrl);
    } else if (webpage?.image) {
      setImageUrl(webpage.image);
    } else {
      setImageUrl(null);
    }
  }, [webpage?.image_storage_path, webpage?.image]);
  
  // Reset both image and favicon visibility when webpage changes
  useEffect(() => {
    const isCached = imageUrl ? loadedImageCache.has(imageUrl) : false;
    setIsImageVisible(true);
    // Only reset isImageLoaded if image is not already cached
    setIsImageLoaded(isCached);
    setFaviconReset(prev => !prev);
    // Only reset internal editing state if using internal control
    if (isEditingExternal === undefined) {
      setInternalIsEditing(false);
    }
  }, [webpage?.id, imageUrl, isEditingExternal]);

  // Initialize edit form when entering edit mode
  useEffect(() => {
    if (isEditing && webpage) {
      setEditedTitle(webpage.title || '');
      setEditedDescription(webpage.description || '');
      setEditedSiteName(webpage.site_name || '');
      setEditedUrl(webpage.url || '');
      setEditedAuthor(webpage.author || '');
      setEditedNotes(webpage.notes || '');
      setEditedContent(webpage.content || '');
    }
  }, [isEditing, webpage]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, [setIsEditing]);

  const handleCopyLink = useCallback(() => {
    if (!webpage) return;
    navigator.clipboard.writeText(webpage.url);
    toast({
      title: 'Link copied',
      description: 'URL copied to clipboard',
    });
  }, [webpage, toast]);

  const handleOpenInNewTab = useCallback(() => {
    if (!webpage) return;
    window.open(webpage.url, '_blank', 'noopener,noreferrer');
  }, [webpage]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  const handleSave = useCallback(async () => {
    if (!webpage) return;
    
    setIsSaving(true);
    try {
      await updateWebpage(webpage.id, {
        title: editedTitle,
        description: editedDescription,
        site_name: editedSiteName,
        url: editedUrl,
        author: editedAuthor,
        notes: editedNotes || null,
        content: editedContent,
      });
      setIsEditing(false);
      toast({
        title: 'Saved',
        description: 'Webpage updated successfully',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save changes',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  }, [webpage, editedTitle, editedDescription, editedSiteName, editedUrl, editedAuthor, editedNotes, editedContent, updateWebpage, toast, setIsEditing]);

  const handleDelete = useCallback(async () => {
    if (!webpage) return;
    
    setIsDeleting(true);
    try {
      await deleteWebpage(webpage.id);
      setShowDeleteDialog(false);
      onWebpageDeleted?.();
      toast({
        title: 'Deleted',
        description: 'Webpage removed successfully',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete webpage',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [webpage, deleteWebpage, onWebpageDeleted, toast]);

  // Keyboard shortcuts for edit mode (CMD+. to cancel, CMD+SHIFT+ENTER to save)
  useEffect(() => {
    if (!isEditing || showFloatingActions) return; // Only for desktop edit mode
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // CMD+. to cancel (common macOS cancel shortcut)
      if (e.key === '.' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleCancelEdit();
      }
      // CMD+SHIFT+ENTER to save
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        handleSave();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, showFloatingActions, handleCancelEdit, handleSave]);

  if (!webpage) return null;

  return (
    <>
      {/* Outer wrapper for positioning floating buttons outside scrollable area */}
      <div className="relative h-full flex flex-col">
        {/* Desktop edit mode buttons - floating outside on the right, stacked vertically */}
        {/* Must be outside the scrollable container to avoid clipping */}
        {!showFloatingActions && isEditing && (
          <div className="absolute top-2 -right-[72px] z-10 flex flex-col gap-1.5">
            <Button
              variant="default"
              size="sm"
              className="h-9 pl-2.5 pr-2 text-xs shadow-sm justify-between w-[120px]"
              onClick={handleSave}
              disabled={isSaving}
            >
              <span className="flex items-left gap-1.5">
                {isSaving ? (
                  <Spinner className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" weight="bold" />
                )}
                Save
              </span>
              <kbd className="inline-flex h-5 ml-2 items-center justify-center gap-0.5 rounded bg-primary-foreground/20 px-1 text-primary-foreground">
                <Command className="h-3 w-3" weight="bold" />
                <ArrowFatUp className="h-3 w-3" weight="bold" />
                <ArrowElbowDownLeft className="h-3 w-3" weight="bold" />
              </kbd>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-9 pl-2.5 pr-2 text-xs bg-white shadow-sm justify-between w-[120px]"
              onClick={handleCancelEdit}
              disabled={isSaving}
            >
              <span className="flex items-left gap-1.5">
                <X className="h-4 w-4" weight="bold" />
                Cancel
              </span>
              <kbd className="inline-flex ml-2 h-5 items-center justify-center gap-0.5 rounded bg-neutral-100 px-1 text-neutral-600">
                <Command className="h-3 w-3" weight="bold" />
                <span className="text-[10px] font-medium">.</span>
              </kbd>
            </Button>
          </div>
        )}

        {/* O hint floating outside on the right - show when selected and not editing */}
        {!showFloatingActions && !isEditing && isSelected && (
          <kbd className={`absolute top-3.5 -right-8 z-10 inline-flex h-5 w-5 items-center justify-center rounded bg-neutral-100 text-[10px] font-medium text-neutral-600 ${isHintFaded ? 'opacity-40' : ''}`}>
            O
          </kbd>
        )}

        {/* Sticky header - stays visible when scrolling */}
        <div className={`shrink-0 lg:px-4 lg:pt-4 pb-3 transition-shadow duration-150 ${isScrolled ? 'shadow' : ''}`}>
          <div className="flex items-start gap-3">
            <Favicon 
              src={webpage.favicon} 
              storagePath={webpage.favicon_storage_path}
              url={webpage.url} 
              className="h-10 w-10 shrink-0 mt-0.5"
              reset={faviconReset}
            />
            <div className="flex flex-col min-w-0 flex-1 pr-10">
              {/* Site name row */}
              <div className="min-h-[1.5rem]">
                {isEditing ? (
                  <Input
                    value={editedSiteName}
                    onChange={(e) => setEditedSiteName(e.target.value)}
                    className="text-base font-semibold h-6 py-0 px-1"
                    placeholder="Site name"
                  />
                ) : (
                  <h2 className="text-base font-semibold truncate text-neutral-900">{webpage.site_name}</h2>
                )}
              </div>
              {/* URL row */}
              <div className="min-h-[1.25rem]">
                {isEditing ? (
                  <Input
                    value={editedUrl}
                    onChange={(e) => setEditedUrl(e.target.value)}
                    className="text-base h-5 py-0 px-1 text-primary"
                    placeholder="https://..."
                  />
                ) : (
                  <a 
                    href={webpage.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm block truncate"
                    title={webpage.url}
                  >
                    {webpage.url}
                  </a>
                )}
              </div>
            </div>
            {/* Menu in header - only show when selected and not in edit mode */}
            {!hideMenu && isSelected && !isEditing && (
              <div 
                className="shrink-0"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <DropdownMenu 
                  open={isDropdownOpen}
                  onOpenChange={(open) => {
                    setIsDropdownOpen(open);
                    onDropdownOpenChange?.(open);
                  }}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 shadow-none hover:bg-neutral-200 ${isDropdownOpen ? 'bg-neutral-200' : ''}`}
                    >
                      <DotsThree className="h-5 w-5" weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[200px]">
                    <DropdownMenuItem onClick={() => setIsDropdownOpen(false)} className="cursor-pointer justify-between">
                      <span className="flex items-center">
                        <X className="h-4 w-4 mr-2" weight="bold" />
                        Close
                      </span>
                      <kbd className="ml-auto text-[10px] text-neutral-600">O</kbd>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleEdit} className="cursor-pointer justify-between">
                      <span className="flex items-center">
                        <PencilSimple className="h-4 w-4 mr-2" weight="bold" />
                        Edit
                      </span>
                      <kbd className="ml-auto text-[10px] text-neutral-600">E</kbd>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer justify-between">
                      <span className="flex items-center">
                        <Link className="h-4 w-4 mr-2" weight="bold" />
                        Copy link
                      </span>
                      <kbd className="ml-auto text-[10px] text-neutral-600">L</kbd>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleOpenInNewTab} className="cursor-pointer justify-between">
                      <span className="flex items-center">
                        <ArrowSquareOut className="h-4 w-4 mr-2" weight="bold" />
                        Open in new tab
                      </span>
                      <kbd className="ml-auto text-[10px] text-neutral-600">T</kbd>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive focus:text-destructive cursor-pointer justify-between"
                    >
                      <span className="flex items-center">
                        <Trash className="h-4 w-4 mr-2" weight="bold" />
                        Delete
                      </span>
                      <kbd className="ml-auto flex items-center gap-0.5 text-neutral-600"><Command className="h-3 w-3" weight="bold" /><span className="text-[10px]">D</span></kbd>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable content area */}
        <div ref={scrollContainerRef} data-details-scroll className="flex-1 overflow-y-auto lg:px-4 lg:pb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className={`space-y-3 ${showFloatingActions && isEditing ? 'pb-16' : ''}`}>
          {/* Main content section */}
          <div className="space-y-0.5">
            {/* Title */}
            <div className="min-h-[1.75rem]">
              {isEditing ? (
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-base lg:text-lg font-semibold h-7 py-0 px-1"
                  placeholder="Title"
                />
              ) : (
                <h1 className={`text-lg font-semibold text-neutral-900 ${!isSelected ? 'line-clamp-1' : ''}`}>{webpage.title}</h1>
              )}
            </div>

            {/* Description */}
            <div className="min-h-[2.5rem]">
              {isEditing ? (
                <Textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  className="text-base resize-none py-1 px-1"
                  placeholder="Description"
                  rows={2}
                />
              ) : (
                webpage.description && (
                  <p className={`text-sm text-neutral-700 ${!isSelected ? 'line-clamp-2' : ''}`}>
                    {webpage.description}
                  </p>
                )
              )}
            </div>
          </div>

          {/* Image section - supports both direct URLs and Supabase Storage paths */}
          {imageUrl && isImageVisible && (
            <div className="relative w-full pb-[56.25%] rounded border border-neutral-200 bg-neutral-50 overflow-hidden">
              {/* Skeleton placeholder while image loads */}
              {!isImageLoaded && (
                <div className="absolute inset-0 bg-neutral-200 animate-pulse" />
              )}
              <img
                src={imageUrl}
                alt={webpage.title || 'Webpage thumbnail'}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                loading="lazy"
                decoding="async"
                onLoad={() => {
                  // Add to cache for future navigations
                  if (imageUrl) loadedImageCache.add(imageUrl);
                  setIsImageLoaded(true);
                }}
                onError={() => setIsImageVisible(false)}
              />
            </div>
          )}

          {/* Notes section */}
          {isEditing ? (
            <div className="min-h-[4rem]">
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 overflow-visible">
                <p className="text-sm font-semibold text-primary mb-2">Your notes</p>
                <Textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  className="text-base resize-none bg-white border border-primary/20 rounded-md p-2 focus-visible:ring-primary/30 text-neutral-800 min-h-[80px]"
                  placeholder="Add notes..."
                />
              </div>
            </div>
          ) : (
            webpage.notes && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <p className="text-sm font-semibold text-primary mb-1">Your notes</p>
                <p className="text-sm text-primary/80 whitespace-pre-wrap">{webpage.notes}</p>
              </div>
            )
          )}

          <div className="space-y-2 pt-1">
            {/* Content section */}
            <p className="text-lg font-semibold text-neutral-900">
              Page content
            </p>
            {isEditing ? (
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="text-base resize-y min-h-[300px] rounded-lg"
                placeholder="Page content (supports Markdown)"
              />
            ) : (
              webpage.content && (
                <div className="max-w-none text-sm text-neutral-700">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Hide inline images - they'll be shown in carousel
                      img: () => null,
                      // Headings: bold but same font size
                      h1: ({ children }) => <p className="font-bold mt-3 mb-1">{children}</p>,
                      h2: ({ children }) => <p className="font-bold mt-3 mb-1">{children}</p>,
                      h3: ({ children }) => <p className="font-semibold mt-2 mb-1">{children}</p>,
                      h4: ({ children }) => <p className="font-semibold mt-2 mb-1">{children}</p>,
                      h5: ({ children }) => <p className="font-medium mt-1.5 mb-0.5">{children}</p>,
                      h6: ({ children }) => <p className="font-medium mt-1.5 mb-0.5">{children}</p>,
                      // Lists with proper styling
                      ul: ({ children }) => <ul className="list-disc pl-4 my-1 space-y-0.5">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal pl-4 my-1 space-y-0.5">{children}</ol>,
                      li: ({ children }) => <li>{children}</li>,
                      // Paragraphs with spacing
                      p: ({ children }) => <p className="mb-2">{children}</p>,
                      // Links
                      a: ({ href, children }) => (
                        <a 
                          href={href} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline break-all"
                        >
                          {children}
                        </a>
                      ),
                      // Code blocks
                      pre: ({ children }) => (
                        <pre className="bg-neutral-100 p-3 rounded-lg overflow-x-auto text-xs my-2">
                          {children}
                        </pre>
                      ),
                      code: ({ children, className }) => {
                        const isInline = !className;
                        return isInline ? (
                          <code className="bg-neutral-100 px-1.5 py-0.5 rounded text-xs font-mono">
                            {children}
                          </code>
                        ) : (
                          <code className="font-mono">{children}</code>
                        );
                      },
                      // Tables
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full border-collapse border border-neutral-200">
                            {children}
                          </table>
                        </div>
                      ),
                      th: ({ children }) => (
                        <th className="border border-neutral-200 px-3 py-2 bg-neutral-50 text-left font-semibold">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border border-neutral-200 px-3 py-2">
                          {children}
                        </td>
                      ),
                      // Blockquotes
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-neutral-300 pl-4 italic text-neutral-600 my-4">
                          {children}
                        </blockquote>
                      ),
                      // Horizontal rules
                      hr: () => <hr className="my-4 border-neutral-200" />,
                      // Strong/bold
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    }}
                  >
                    {webpage.content}
                  </ReactMarkdown>
                  
                  {/* Image carousel at bottom of content */}
                  {contentImages.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-neutral-100">
                      <p className="text-xs font-medium text-neutral-500 mb-2">
                        {contentImages.length} image{contentImages.length > 1 ? 's' : ''} from page
                      </p>
                      <Carousel opts={{ align: "start", dragFree: true }} className="w-full">
                        <CarouselContent className="-ml-2">
                          {contentImages.map((img, idx) => (
                            <CarouselItem key={idx} className="basis-1/3 pl-2">
                              <button
                                type="button"
                                onClick={() => setLightboxImageIndex(idx)}
                                className="group relative aspect-video w-full rounded border border-neutral-200 overflow-hidden bg-neutral-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                              >
                                <img
                                  src={img.src}
                                  alt={img.alt}
                                  loading="lazy"
                                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                  onError={(e) => {
                                    const parent = e.currentTarget.parentElement;
                                    if (parent) parent.style.display = 'none';
                                  }}
                                />
                                {/* Hover overlay with expand icon */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                                  <ArrowsOut className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" weight="bold" />
                                </div>
                              </button>
                            </CarouselItem>
                          ))}
                        </CarouselContent>
                        {contentImages.length > 3 && (
                          <>
                            <CarouselPrevious className="left-0 h-7 w-7 bg-white/90 hover:bg-white border-neutral-200" />
                            <CarouselNext className="right-0 h-7 w-7 bg-white/90 hover:bg-white border-neutral-200" />
                          </>
                        )}
                      </Carousel>
                    </div>
                  )}
                </div>
              )
            )}
          </div>

          {/* Author */}
          <div className="min-h-[1.25rem]">
            {isEditing ? (
              <div className="flex items-center gap-1">
                <span className="text-sm text-neutral-700">By</span>
                <Input
                  value={editedAuthor}
                  onChange={(e) => setEditedAuthor(e.target.value)}
                  className="text-base h-5 py-0 px-1 flex-1"
                  placeholder="Author"
                />
              </div>
            ) : (
              webpage.author && (
                <p className="text-sm text-neutral-700">
                  By {webpage.author}
                </p>
              )
            )}
          </div>
        </div>
      </div>
      {/* End of outer wrapper */}
      </div>

      {/* Mobile floating action buttons */}
      {showFloatingActions && isEditing && (
        <div className="fixed bottom-0 left-0 right-0 px-4 py-2 bg-white border-t border-neutral-200 flex gap-2 z-50">
          <Button
            variant="ghost"
            className="flex-1 h-10 text-neutral-600 gap-1.5"
            onClick={handleCancelEdit}
            disabled={isSaving}
          >
            <X className="h-4 w-4" weight="bold" />
            Cancel
          </Button>
          <Button
            className="flex-1 h-10 gap-1.5"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Spinner className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" weight="bold" />
            )}
            {isSaving ? 'Saving' : 'Save'}
          </Button>
        </div>
      )}

      <DeleteWebpageDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        webpageTitle={webpage.title}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />

      {/* Image lightbox dialog */}
      <Dialog 
        open={lightboxImageIndex !== null} 
        onOpenChange={(open) => !open && setLightboxImageIndex(null)}
      >
        <DialogContent className="max-w-5xl w-[90vw] min-h-[60vh] max-h-[90vh] p-0 bg-black/95 border-none overflow-visible [&>button.absolute.right-4]:hidden">
          <VisuallyHidden>
            <DialogTitle>Image preview</DialogTitle>
          </VisuallyHidden>
          {/* Custom close button - positioned in top-right corner inside dialog */}
          <button
            type="button"
            onClick={() => setLightboxImageIndex(null)}
            className="absolute right-3 top-3 z-[60] h-10 w-10 rounded-full bg-neutral-600 hover:bg-neutral-500 flex items-center justify-center transition-colors shadow-lg"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-white" weight="bold" />
          </button>
          {lightboxImageIndex !== null && contentImages[lightboxImageIndex] && (
            <div className="relative w-full h-full min-h-[60vh] flex items-center justify-center p-8">
              <img
                src={contentImages[lightboxImageIndex].src}
                alt={contentImages[lightboxImageIndex].alt}
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
              {/* Navigation buttons - positioned on edges of dialog, outside content */}
              {contentImages.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => setLightboxImageIndex((lightboxImageIndex - 1 + contentImages.length) % contentImages.length)}
                    className="absolute -left-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-neutral-600 hover:bg-neutral-500 flex items-center justify-center transition-colors shadow-lg"
                    aria-label="Previous image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setLightboxImageIndex((lightboxImageIndex + 1) % contentImages.length)}
                    className="absolute -right-6 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-neutral-600 hover:bg-neutral-500 flex items-center justify-center transition-colors shadow-lg"
                    aria-label="Next image"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </button>
                </>
              )}
              {/* Image counter */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm bg-black/50 px-3 py-1 rounded-full">
                {lightboxImageIndex + 1} / {contentImages.length}
              </div>
              {/* Alt text if available */}
              {contentImages[lightboxImageIndex].alt && (
                <div className="absolute bottom-14 left-1/2 -translate-x-1/2 max-w-md text-center text-white/60 text-xs px-4">
                  {contentImages[lightboxImageIndex].alt}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
